import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Switch from "react-switch";
import "./fullhousesetting.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";
import TabMenu from "../tabmenu/TabMenu";

const parseBool = (val) => val === "true" || val === true;

const blockNonNumericNumberKeys = (e) => {
  if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E") {
    e.preventDefault();
  }
};

const parseObject = (val) => {
  if (val == null) return {};
  if (typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return typeof p === "object" && p !== null && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  return {};
};

const DEFAULT_CLAIMS = {
  top_line: true,
  middle_line: true,
  bottom_line: true,
  four_corners: true,
  full_house: true,
  early_five: true,
};

const DEFAULT_WINNER_LIST = {
  top_line: 5,
  middle_line: 5,
  bottom_line: 5,
  four_corners: 5,
  full_house: 5,
  early_five: 5,
  no_luck: 5,
  unlucky_five: 5,
};

const FullHouseSetting = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [preventSubmit, setPreventSubmit] = useState(false);
  const [preventClaim, setPreventClaim] = useState(false);
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [displayLeaderboard, setDisplayLeaderboard] = useState(true);
  const [multipleClaims, setMultipleClaims] = useState(true);
  const [winnerRestrictions, setWinnerRestrictions] = useState(false);
  const [numlimit, setNumlimit] = useState(5);
  const [claims, setClaims] = useState(DEFAULT_CLAIMS);
  const [winnerList, setWinnerList] = useState(DEFAULT_WINNER_LIST);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setPreventSubmit(parseBool(data.prevent_submit));
    setPreventClaim(parseBool(data.prevent_claim));
    setAutoGenerate(parseBool(data.auto_generate));
    setDisplayLeaderboard(parseBool(data.display_leaderboard));
    setMultipleClaims(parseBool(data.multiple_claims));
    setWinnerRestrictions(parseBool(data.winner_restrictions));
    const n = Number(data.numlimit);
    setNumlimit(Number.isNaN(n) ? 5 : Math.min(10, Math.max(1, n)));
    setClaims({ ...DEFAULT_CLAIMS, ...parseObject(data.claims) });
    setWinnerList({ ...DEFAULT_WINNER_LIST, ...parseObject(data.winnerList) });
  }, [data]);

  const toStr = (b) => (b ? "true" : "false");

  const updateToggle = async (key, value) => {
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { [key]: toStr(value) },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to update", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (numlimit === "" || numlimit == null) {
      Swal.fire("Error", "Release limit should be between 1 and 10", "error");
      return;
    }
    const num = Number(numlimit);
    if (Number.isNaN(num) || num < 1 || num > 10) {
      Swal.fire("Error", "Release limit should be between 1 and 10", "error");
      return;
    }
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { numlimit: num },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Success", "Data Updated", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWinners = async () => {
    const getData = { ...winnerList };
    const claimKeys = Object.keys(claims).filter((k) => claims[k]);
    let invalid = false;
    for (const key of claimKeys) {
      const v = getData[key];
      const n = Number(v);
      if (v === "" || Number.isNaN(n) || n < 1 || n > 10) {
        invalid = true;
        break;
      }
      getData[key] = n;
    }
    if (invalid) {
      Swal.fire("Error", "Please enter a value between 1 and 10 for all inputs.", "error");
      return;
    }
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { winnerList: getData },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Success", "Data Updated", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClaimToggle = async (claimType, value) => {
    const next = { ...claims, [claimType]: value };
    setClaims(next);
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { claims: next },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
    } catch (e) {
      setClaims(claims);
      Swal.fire("Error", e.message || "Error updating claim", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleWinnerChange = (key, val) => {
    setWinnerList((prev) => ({ ...prev, [key]: val }));
  };

  const handleWinnerNumberChange = (key, raw) => {
    if (raw === "") {
      handleWinnerChange(key, "");
      return;
    }
    const n = Number(raw);
    if (Number.isNaN(n) || n < 0) return;
    handleWinnerChange(key, Math.min(10, Math.max(1, n)));
  };

  const handleNumlimitChange = (raw) => {
    if (raw === "") {
      setNumlimit("");
      return;
    }
    const n = Number(raw);
    if (Number.isNaN(n) || n < 0) return;
    setNumlimit(Math.min(10, Math.max(1, n)));
  };

  if (status === "loading" && !data) {
    return (
      <div className="fullhousesetting-page">
        <div className="fullhousesetting-loading">Loading...</div>
      </div>
    );
  }

  const claimEntries = Object.entries(claims);
  const winnerRestrictionEntries = winnerRestrictions
    ? claimEntries.filter(([, enabled]) => enabled)
    : [];

  return (
    <>
      <div className="back-button-holder">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/rules")}
        >
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <TabMenu />

      <div className="fullhousesetting-page">
        <div className="fullhousesetting-row">
          <div className="fullhousesetting-col fullhousesetting-col-left">
            <div className="fullhousesetting-card">
              <div className="fullhousesetting-card-header">Full House Settings</div>
              <div className="fullhousesetting-card-body">
                <div className="fullhousesetting-form-group">
                  <Switch
                    checked={preventSubmit}
                    onChange={(c) => {
                      setPreventSubmit(c);
                      updateToggle("prevent_submit", c);
                    }}
                    disabled={loading}
                    height={22}
                    width={44}
                  />
                  <label className="fullhousesetting-label">Disable Ticket Creation</label>
                </div>
                <div className="fullhousesetting-form-group">
                  <Switch
                    checked={preventClaim}
                    onChange={(c) => {
                      setPreventClaim(c);
                      updateToggle("prevent_claim", c);
                    }}
                    disabled={loading}
                    height={22}
                    width={44}
                  />
                  <label className="fullhousesetting-label">Disable Claims</label>
                </div>
                <div className="fullhousesetting-form-group">
                  <Switch
                    checked={autoGenerate}
                    onChange={(c) => {
                      setAutoGenerate(c);
                      updateToggle("auto_generate", c);
                    }}
                    disabled={loading}
                    height={22}
                    width={44}
                  />
                  <label className="fullhousesetting-label">Disable Auto Generate Button</label>
                </div>
                <div className="fullhousesetting-form-group">
                  <Switch
                    checked={displayLeaderboard}
                    onChange={(c) => {
                      setDisplayLeaderboard(c);
                      updateToggle("display_leaderboard", c);
                    }}
                    disabled={loading}
                    height={22}
                    width={44}
                  />
                  <label className="fullhousesetting-label">Show Leaderboard to everyone</label>
                </div>
                <div className="fullhousesetting-form-group">
                  <Switch
                    checked={multipleClaims}
                    onChange={(c) => {
                      setMultipleClaims(c);
                      updateToggle("multiple_claims", c);
                    }}
                    disabled={loading}
                    height={22}
                    width={44}
                  />
                  <label className="fullhousesetting-label">Multiple Claims</label>
                </div>
                <div className="fullhousesetting-form-group">
                  <Switch
                    checked={winnerRestrictions}
                    onChange={(c) => {
                      setWinnerRestrictions(c);
                      updateToggle("winner_restrictions", c);
                    }}
                    disabled={loading}
                    height={22}
                    width={44}
                  />
                  <label className="fullhousesetting-label">Winner Restriction</label>
                </div>
                <div className="fullhousesetting-well fullhousesetting-well-warn">
                  Toggle switch to Disable / Enable
                </div>
              </div>
            </div>
          </div>

          <div className="fullhousesetting-col fullhousesetting-col-center">
            <div className="fullhousesetting-card fullhousesetting-card-scroll">
              <div className="fullhousesetting-card-header">Full House Settings</div>
              <div className="fullhousesetting-card-body">
                <div className="fullhousesetting-field">
                  <h4 className="fullhousesetting-title">Release Limit In Numbers</h4>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    step={1}
                    value={numlimit === "" ? "" : numlimit}
                    onKeyDown={blockNonNumericNumberKeys}
                    onChange={(e) => handleNumlimitChange(e.target.value)}
                    className="fullhousesetting-input"
                  />
                  <p className="fullhousesetting-muted">
                    <code>* Do not Change After Release<br />* The release limit for words/numbers can be from 1 to 10.</code>
                  </p>
                </div>
                <div className="fullhousesetting-actions">
                  <button
                    type="button"
                    className="fullhousesetting-btn fullhousesetting-btn-primary"
                    onClick={handleSaveSettings}
                    disabled={loading}
                  >
                    Save
                  </button>
                </div>

                {winnerRestrictions && winnerRestrictionEntries.length > 0 && (
                  <>
                    <h4 className="fullhousesetting-title fullhousesetting-title-center">Winner Restrictions</h4>
                    <div className="fullhousesetting-winner-grid">
                      {winnerRestrictionEntries.map(([key]) => (
                        <div key={key} className="fullhousesetting-winner-row">
                          <label className="fullhousesetting-winner-label">{key}</label>
                          <input
                            type="number"
                            min={1}
                            max={10}
                            step={1}
                            value={winnerList[key] ?? ""}
                            onKeyDown={blockNonNumericNumberKeys}
                            onChange={(e) => handleWinnerNumberChange(key, e.target.value)}
                            className="fullhousesetting-input fullhousesetting-claim-input"
                            placeholder="Number"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="fullhousesetting-muted">
                      <code>* Winner restriction can not be 0 or negative.</code>
                    </p>
                    <div className="fullhousesetting-actions">
                      <button
                        type="button"
                        className="fullhousesetting-btn fullhousesetting-btn-primary"
                        onClick={handleSaveWinners}
                        disabled={loading}
                      >
                        Save
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="fullhousesetting-col fullhousesetting-col-right">
            <div className="fullhousesetting-card">
              <div className="fullhousesetting-card-header">Claims Type</div>
              <div className="fullhousesetting-card-body">
                <h4 className="fullhousesetting-title">Toggle claim types</h4>
                {claimEntries.map(([key, val]) => (
                  <div key={key} className="fullhousesetting-form-group">
                    <Switch
                      checked={!!val}
                      onChange={(c) => handleClaimToggle(key, c)}
                      disabled={loading}
                      height={20}
                      width={36}
                    />
                    <label className="fullhousesetting-label">{key.replace(/_/g, " ")}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FullHouseSetting;
